import { type MoorAppsPlanId } from './pricing';

export type LeadRequestType = 'contact' | 'demo';

export type WebsiteLead = {
  requestType?: LeadRequestType;
  name: string;
  email: string;
  phone?: string;
  message: string;
  interest: string;
  teamSize?: string;
  company?: string;
};

export type WebsiteLeadResult = {
  ok: boolean;
  message: string;
};

type LeadApiResponse = {
  success?: boolean;
  message?: string;
};

type CommerceApiResponse<T = unknown> = {
  success?: boolean;
  status?: string;
  message?: string;
} & T;

type LicenseDownloadApiSummary = {
  status?: string;
  plan_id?: MoorAppsPlanId;
  plan_name?: string;
  account_email?: string | null;
  license_id?: string | null;
  issued_at?: string | null;
  expires_at?: string | null;
};

export type CheckoutSessionResult = WebsiteLeadResult & {
  url?: string;
  sessionId?: string;
};

export type PurchaseSession = {
  purchase_id: string;
  session_id: string;
  plan_id: MoorAppsPlanId;
  plan_name: string;
  display_price: string;
  status: 'checkout_created' | 'paid' | 'provisioned';
  customer_email?: string;
  paid_at?: string | null;
  provisioned_at?: string | null;
  access_email_status?: string | null;
  license_email_status?: string | null;
  license_email_error?: string | null;
};

export type PurchaseSessionResult = WebsiteLeadResult & {
  purchase?: PurchaseSession;
  status?: string;
};

export type SignupPayload = {
  sessionId: string;
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
};

export type SignupResult = WebsiteLeadResult & {
  downloadUrl?: string;
  purchase?: PurchaseSession;
  status?: string;
};

export type DownloadOption = {
  platform: string;
  label: string;
  url: string;
  available: boolean;
};

export type LicenseDownloadSummary = {
  status: string;
  planId?: MoorAppsPlanId;
  planName: string;
  accountEmail?: string | null;
  licenseId?: string | null;
  issuedAt?: string | null;
  expiresAt?: string | null;
};

export type DownloadResult = WebsiteLeadResult & {
  downloads: DownloadOption[];
  purchase?: PurchaseSession;
  license?: LicenseDownloadSummary;
  status?: string;
  supportEmail?: string;
};

function normalizeLicenseSummary(
  license?: LicenseDownloadApiSummary | LicenseDownloadSummary,
): LicenseDownloadSummary | undefined {
  if (!license) return undefined;

  if ('planName' in license) {
    return license;
  }

  return {
    status: license.status || '',
    planId: license.plan_id,
    planName: license.plan_name || 'Moor Apps',
    accountEmail: license.account_email,
    licenseId: license.license_id,
    issuedAt: license.issued_at,
    expiresAt: license.expires_at,
  };
}

function getLeadEndpoint(requestType: LeadRequestType): string {
  return requestType === 'contact' ? '/api/contact' : '/api/demo';
}

function normalizeRequestType(value: unknown): LeadRequestType {
  return value === 'contact' ? 'contact' : 'demo';
}

export async function submitWebsiteLead(payload: WebsiteLead): Promise<WebsiteLeadResult> {
  const requestType = normalizeRequestType(payload.requestType);

  try {
    const response = await fetch(getLeadEndpoint(requestType), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...payload,
        requestType,
        request_type: requestType,
        source: 'moor-engineers-website',
      }),
    });

    const result = (await response.json().catch(() => null)) as LeadApiResponse | null;

    if (!response.ok) {
      return {
        ok: false,
        message: result?.message || `Request could not be sent (${response.status}).`,
      };
    }

    return {
      ok: result?.success !== false,
      message: result?.message || 'Request sent.',
    };
  } catch {
    return {
      ok: false,
      message: 'The email service is not running yet. Please try again in a moment.',
    };
  }
}

export async function createCheckoutSession(
  planId: MoorAppsPlanId,
): Promise<CheckoutSessionResult> {
  try {
    const response = await fetch('/api/checkout/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ planId }),
    });
    const result = (await response.json().catch(() => null)) as CommerceApiResponse<{
      url?: string;
      session_id?: string;
    }> | null;

    if (!response.ok || !result?.success) {
      return {
        ok: false,
        message: result?.message || `Checkout could not be started (${response.status}).`,
      };
    }

    return {
      ok: true,
      message: result.message || 'Checkout started.',
      url: result.url,
      sessionId: result.session_id,
    };
  } catch {
    return {
      ok: false,
      message: 'The checkout service is not running yet. Please try again in a moment.',
    };
  }
}

export async function getPurchaseSession(sessionId: string): Promise<PurchaseSessionResult> {
  if (!sessionId) {
    return {
      ok: false,
      message: 'A valid checkout session is required.',
      status: 'missing_session',
    };
  }

  try {
    const response = await fetch(`/api/purchase/session/${encodeURIComponent(sessionId)}`);
    const result = (await response.json().catch(() => null)) as CommerceApiResponse<{
      purchase?: PurchaseSession;
    }> | null;

    if (!response.ok || !result?.success || !result.purchase) {
      return {
        ok: false,
        message: result?.message || `Purchase could not be verified (${response.status}).`,
        status: result?.status,
      };
    }

    return {
      ok: true,
      message: result.message || 'Purchase verified.',
      purchase: result.purchase,
      status: result.status,
    };
  } catch {
    return {
      ok: false,
      message: 'The purchase verification service is not running yet.',
      status: 'service_unavailable',
    };
  }
}

export async function completeSignup(payload: SignupPayload): Promise<SignupResult> {
  try {
    const response = await fetch('/api/signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: payload.sessionId,
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        company: payload.company,
      }),
    });
    const result = (await response.json().catch(() => null)) as CommerceApiResponse<{
      download_url?: string;
      purchase?: PurchaseSession;
    }> | null;

    if (!response.ok || !result?.success) {
      return {
        ok: false,
        message: result?.message || `Account setup could not be completed (${response.status}).`,
        status: result?.status,
      };
    }

    return {
      ok: true,
      message: result.message || 'Account setup complete.',
      downloadUrl: result.download_url,
      purchase: result.purchase,
      status: result.status,
    };
  } catch {
    return {
      ok: false,
      message: 'The account setup service is not running yet.',
      status: 'service_unavailable',
    };
  }
}

export async function getDownloadInfo(sessionId: string): Promise<DownloadResult> {
  if (!sessionId) {
    return {
      ok: true,
      message: 'Enter your license key to unlock the Moor Apps download.',
      downloads: [],
      status: 'license_required',
    };
  }

  try {
    const response = await fetch(`/api/download?session_id=${encodeURIComponent(sessionId)}`);
    const result = (await response.json().catch(() => null)) as CommerceApiResponse<{
      downloads?: DownloadOption[];
      purchase?: PurchaseSession;
      support_email?: string;
      license?: LicenseDownloadApiSummary;
    }> | null;

    if (!response.ok || !result?.success) {
      return {
        ok: false,
        message: result?.message || `Download information could not be loaded (${response.status}).`,
        downloads: [],
        purchase: result?.purchase,
        license: normalizeLicenseSummary(result?.license),
        status: result?.status,
        supportEmail: result?.support_email,
      };
    }

    return {
      ok: true,
      message: result.message || 'Download information loaded.',
      downloads: result.downloads || [],
      purchase: result.purchase,
      license: normalizeLicenseSummary(result.license),
      status: result.status,
      supportEmail: result.support_email,
    };
  } catch {
    return {
      ok: false,
      message: 'The download service is not running yet.',
      downloads: [],
      status: 'service_unavailable',
    };
  }
}

export async function verifyLicenseDownload(licenseKey: string): Promise<DownloadResult> {
  const normalizedLicenseKey = licenseKey.trim();

  if (!normalizedLicenseKey) {
    return {
      ok: false,
      message: 'Enter your Moor Apps license key.',
      downloads: [],
      status: 'missing_license',
    };
  }

  try {
    const response = await fetch('/api/download/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        license_token: normalizedLicenseKey,
        licenseKey: normalizedLicenseKey,
      }),
    });
    const result = (await response.json().catch(() => null)) as CommerceApiResponse<{
      downloads?: DownloadOption[];
      license?: LicenseDownloadApiSummary;
      support_email?: string;
    }> | null;

    if (!response.ok || !result?.success) {
      return {
        ok: false,
        message: result?.message || `License could not be verified (${response.status}).`,
        downloads: [],
        license: normalizeLicenseSummary(result?.license),
        status: result?.status,
        supportEmail: result?.support_email,
      };
    }

    return {
      ok: true,
      message: result.message || 'License verified. Downloads are ready.',
      downloads: result.downloads || [],
      license: normalizeLicenseSummary(result.license),
      status: result.status,
      supportEmail: result.support_email,
    };
  } catch {
    return {
      ok: false,
      message: 'The license verification service is not running yet.',
      downloads: [],
      status: 'service_unavailable',
    };
  }
}
